import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { HeatmapType } from '@backoffice-frontend/graphql'
import * as maplibregl from 'maplibre-gl'
import { useCallback, useEffect, useRef } from 'react'
import {
  useRouteBuilderHeatmapDataLazyQuery,
  useRouteBuilderHeatmapFiltersQuery,
} from '../Heatmap.hook'
import type { HeatmapFilters } from './HeatmapFilterModal'

// Layer / source IDs — one set per heatmap type
const SOURCE_ID = {
  [HeatmapType.HeatmapTypePotentialCriticalEvents]: 'heatmap-pce-source',
  [HeatmapType.HeatmapTypeDisengagements]: 'heatmap-dis-source',
  [HeatmapType.HeatmapTypeGeonetCoverage]: 'heatmap-cov-source',
} as const

const LAYER_HEAT = {
  [HeatmapType.HeatmapTypePotentialCriticalEvents]: 'heatmap-pce-heat',
  [HeatmapType.HeatmapTypeDisengagements]: 'heatmap-dis-heat',
  [HeatmapType.HeatmapTypeGeonetCoverage]: 'heatmap-cov-line',
} as const

const CIRCLE_PREFIX = {
  [HeatmapType.HeatmapTypePotentialCriticalEvents]: 'heatmap-pce-circle',
  [HeatmapType.HeatmapTypeDisengagements]: 'heatmap-dis-circle',
  [HeatmapType.HeatmapTypeGeonetCoverage]: '',
} as const

// Colour palettes per heatmap type
// Ramp runs dark purple (sparsest) → navy → dark green → green → light green →
// yellow (most concentrated). Density 0 stays transparent, with a long, gentle
// alpha fade-in at low densities so the heatmap edge dissolves smoothly into the
// map instead of showing a hard boundary. Extra intermediate colour stops (with
// blended midpoint hues) keep every band transition soft rather than banded, so
// the whole gradient reads as one continuous sweep.
const HEAT_COLOR_STOPS = [
  'interpolate',
  ['linear'],
  ['heatmap-density'],
  0,
  'rgba(52,0,66,0)',
  0.03,
  'rgba(52,0,66,0.06)',
  0.07,
  'rgba(52,0,66,0.16)',
  0.12,
  'rgba(52,0,66,0.3)',
  0.18,
  'rgba(52,0,66,0.5)',
  0.24,
  'rgba(52,0,66,0.72)',
  0.3,
  '#340042',
  0.34,
  '#330C4E',
  0.38,
  '#33195B',
  0.4,
  '#312668',
  0.42,
  '#303475',
  0.47,
  '#2C4176',
  0.52,
  '#294F78',
  0.57,
  '#255C79',
  0.62,
  '#216A7B',
  0.66,
  '#227877',
  0.7,
  '#248674',
  0.74,
  '#259470',
  0.78,
  '#26A26C',
  0.815,
  '#3DAE5D',
  0.85,
  '#55BB4F',
  0.885,
  '#6CC740',
  0.92,
  '#84D432',
  0.945,
  '#A2D82D',
  0.97,
  '#C0DD28',
  0.985,
  '#DEE023',
  1,
  '#FCE51E',
] as maplibregl.ExpressionSpecification

// Shared heatmap paint. `heatmap-radius` is in SCREEN PIXELS. A single zoom
// interpolation (MapLibre requires the zoom expression to be the outermost one)
// gives us two behaviours across the threshold zoom 13:
//   • Zoomed OUT (zoom ≤ 13): the radius roughly DOUBLES every zoom level
//     (3.4→6.9→13.75→27.5→55). Doubling the pixel radius while each pixel covers
//     half the ground keeps a near-constant GEOGRAPHIC FOOTPRINT, so the heatmap
//     just scales with the map and its appearance stops changing.
//   • Zoomed IN (zoom ≥ 13): the radius grows gently in pixels (55→75→100), so the
//     heatmap re-blends and behaves the way it used to at closer zoom.
// Intensity and weight are held constant. Both the PCE and disengagement heat
// layers use this identical paint.
//
// Exported so heat layers outside this component (the Route Builder driver-notes
// heatmap) render with the same radius/intensity/colour ramp instead of a second,
// drifting copy. A layer that needs per-feature weighting spreads this and
// overrides `heatmap-weight` alone — everything that governs how density reads at
// a given zoom stays shared.
export const HEAT_PAINT = {
  'heatmap-weight': 0.5,
  'heatmap-intensity': 0.6,
  'heatmap-radius': [
    'interpolate',
    ['linear'],
    ['zoom'],
    9,
    5.6,
    10,
    11.25,
    11,
    22.5,
    12,
    45,
    13,
    90,
    15,
    120,
    18,
    160,
  ] as maplibregl.ExpressionSpecification,
  'heatmap-opacity': [
    'interpolate',
    ['linear'],
    ['zoom'],
    14,
    0.55,
    18,
    0.4,
  ] as maplibregl.ExpressionSpecification,
  'heatmap-color': HEAT_COLOR_STOPS,
} as const

// Category colours are assigned BY INDEX from this ordered palette rather than by
// name — the actual category labels (e.g. "Longitudinal behavior", "Deadlock",
// "Routing issues") vary and don't match a fixed key set, so name-keyed maps made
// everything fall through to a single default colour. Assigning by the category's
// position in the (stable) list gives each sub-item a distinct colour, matching
// admt-web-app. The sidebar legend and the map dots read the same ordered category
// list, so colours stay in sync between them.
const CATEGORY_COLOR_PALETTE = [
  '#e53935', // red
  '#fb8c00', // orange
  '#388e3c', // dark green
  '#1e88e5', // blue
  '#fdd835', // yellow
  '#8e24aa', // purple
  '#43a047', // light green
  '#757575', // gray
  '#00897b', // teal
  '#d81b60', // pink
  '#5e35b1', // deep purple
  '#c0ca33', // lime
]

/** Colour for a category by its index in the ordered category list. */
export const categoryColor = (index: number): string =>
  CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length] ?? '#757575'

// Coverage success-rate colour ramp  0% → 100%
const COV_COLOR_EXPR: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['get', 'successRate'],
  0,
  '#e53935',
  0.2,
  '#f57c00',
  0.6,
  '#ffee58',
  0.75,
  '#26a69a',
  0.88,
  '#66bb6a',
  1.0,
  '#2e7d32',
]

const isMapReady = (map: maplibregl.Map): boolean =>
  // MapLibre sets map.style to null/undefined when the map is destroyed.
  // Calling getLayer/getSource on a destroyed map throws internally.
  !!(map as unknown as { style: unknown }).style

// First basemap label layer (symbol layer with a text-field). Heatmap layers
// are inserted before this so street names and other labels stay legible on top.
export const firstLabelLayerId = (map: maplibregl.Map): string | undefined => {
  const style = map.getStyle()
  if (!style) return undefined
  for (const layer of style.layers) {
    if (
      layer.type === 'symbol' &&
      layer.layout &&
      'text-field' in layer.layout
    ) {
      return layer.id
    }
  }
  return undefined
}

const removeLayer = (map: maplibregl.Map, id: string) => {
  if (!isMapReady(map)) return
  if (map.getLayer(id)) map.removeLayer(id)
}
const removeSource = (map: maplibregl.Map, id: string) => {
  if (!isMapReady(map)) return
  if (map.getSource(id)) map.removeSource(id)
}

// ─── Build GeoJSON from query results ────────────────────────────────────────

type PcePoint = {
  location: { latitude: number; longitude: number } | null
  eventType: string
  softwareVersion: string
  timestampMs: number
  annotationCount: number
}

type DisPoint = {
  location: { latitude: number; longitude: number } | null
  initiator: string
  disengagementReason: string
  softwareVersion: string
  timestampMs: number
}

type CovPoint = {
  successRate: number
  streetName: string
  osmEdgeId: number
  geojsonGeometry: string
  swVersion: string
  avgDuration: number
  maxSpeedKmh: number
  osmEdgeCount: number
}

const buildPceGeoJSON = (
  points: PcePoint[],
): GeoJSON.FeatureCollection<GeoJSON.Point> => ({
  type: 'FeatureCollection',
  features: points
    .filter(p => p.location != null)
    .map(p => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [p.location!.longitude, p.location!.latitude],
      },
      properties: {
        eventType: p.eventType,
        softwareVersion: p.softwareVersion,
        timestampMs: p.timestampMs,
        annotationCount: p.annotationCount,
      },
    })),
})

const buildDisGeoJSON = (
  points: DisPoint[],
): GeoJSON.FeatureCollection<GeoJSON.Point> => ({
  type: 'FeatureCollection',
  features: points
    .filter(p => p.location != null)
    .map(p => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [p.location!.longitude, p.location!.latitude],
      },
      properties: {
        initiator: p.initiator,
        disengagementReason: p.disengagementReason,
        softwareVersion: p.softwareVersion,
        timestampMs: p.timestampMs,
      },
    })),
})

const buildCovGeoJSON = (points: CovPoint[]): GeoJSON.FeatureCollection => {
  const features: GeoJSON.Feature[] = []
  for (const p of points) {
    try {
      const geom = JSON.parse(p.geojsonGeometry) as GeoJSON.Geometry
      features.push({
        type: 'Feature',
        geometry: geom,
        properties: {
          successRate: p.successRate,
          streetName: p.streetName,
          osmEdgeId: p.osmEdgeId,
          swVersion: p.swVersion,
          avgDuration: p.avgDuration,
          maxSpeedKmh: p.maxSpeedKmh,
          osmEdgeCount: p.osmEdgeCount,
        },
      })
    } catch {
      // skip malformed geometry
    }
  }
  return { type: 'FeatureCollection', features }
}

// ─── Map layer management ─────────────────────────────────────────────────────

const addPceLayers = (
  map: maplibregl.Map,
  sourceId: string,
  heatLayerId: string,
  circlePrefix: string,
  categories: string[],
  insertBefore?: string,
) => {
  map.addLayer(
    {
      id: heatLayerId,
      type: 'heatmap',
      source: sourceId,
      maxzoom: 18,
      paint: HEAT_PAINT,
    },
    insertBefore,
  )

  categories.forEach((cat, idx) => {
    const color = categoryColor(idx)
    map.addLayer(
      {
        id: `${circlePrefix}-${cat}`,
        type: 'circle',
        source: sourceId,
        minzoom: 14,
        filter: ['==', ['get', 'eventType'], cat],
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            2,
            18,
            8,
          ] as maplibregl.ExpressionSpecification,
          'circle-color': color,
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            0,
            16,
            0.9,
          ] as maplibregl.ExpressionSpecification,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
          'circle-stroke-opacity': 0.8,
        },
      },
      insertBefore,
    )
  })
}

const addDisLayers = (
  map: maplibregl.Map,
  sourceId: string,
  heatLayerId: string,
  circlePrefix: string,
  initiators: string[],
  insertBefore?: string,
) => {
  map.addLayer(
    {
      id: heatLayerId,
      type: 'heatmap',
      source: sourceId,
      maxzoom: 18,
      paint: HEAT_PAINT,
    },
    insertBefore,
  )

  initiators.forEach((initiator, idx) => {
    const color = categoryColor(idx)
    map.addLayer(
      {
        id: `${circlePrefix}-${initiator}`,
        type: 'circle',
        source: sourceId,
        minzoom: 14,
        filter: ['==', ['get', 'initiator'], initiator],
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            2,
            18,
            8,
          ] as maplibregl.ExpressionSpecification,
          'circle-color': color,
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14,
            0,
            16,
            0.9,
          ] as maplibregl.ExpressionSpecification,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
          'circle-stroke-opacity': 0.8,
        },
      },
      insertBefore,
    )
  })
}

const addCovLayers = (
  map: maplibregl.Map,
  sourceId: string,
  lineLayerId: string,
  insertBefore?: string,
) => {
  map.addLayer(
    {
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': COV_COLOR_EXPR,
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          2,
          16,
          5,
        ] as maplibregl.ExpressionSpecification,
        'line-opacity': 0.6,
      },
    },
    insertBefore,
  )
}

// ─── Hover popup ──────────────────────────────────────────────────────────────

const formatTimestamp = (ms: number): string => {
  const d = new Date(ms)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`
}

const makePopup = (
  map: maplibregl.Map,
  html: string,
  coords: [number, number],
): maplibregl.Popup =>
  new maplibregl.Popup({ closeButton: false, closeOnClick: false })
    .setLngLat(coords)
    .setHTML(html)
    .addTo(map)

// ─── Component ────────────────────────────────────────────────────────────────

export type HeatmapLayerType =
  | 'pce'
  | 'disengagements'
  | 'geonetCoverage'
  | null

export type HeatmapFilterOptions = {
  eventTypes: string[]
  initiators: string[]
  disengagementReasons: string[]
  softwareVersions: string[]
  earliestTimestampMs: number | null
  latestTimestampMs: number | null
}

type HeatmapLayerProps = {
  map: maplibregl.Map | null
  metropolisId: number
  activeType: HeatmapLayerType
  filters: HeatmapFilters
  hiddenCategories?: Set<string>
  onLoadingChange?: (loading: boolean) => void
  onFilterOptionsChange?: (options: HeatmapFilterOptions | null) => void
  onCategoriesChange?: (categories: string[]) => void
  insertBeforeLayer?: string
}

export const HeatmapLayer = ({
  map,
  metropolisId,
  activeType,
  filters,
  hiddenCategories,
  onLoadingChange,
  onFilterOptionsChange,
  onCategoriesChange,
  insertBeforeLayer,
}: HeatmapLayerProps) => {
  const popupRef = useRef<maplibregl.Popup | null>(null)
  const activeTypeRef = useRef(activeType)
  activeTypeRef.current = activeType

  const gqlType =
    activeType === 'pce'
      ? HeatmapType.HeatmapTypePotentialCriticalEvents
      : activeType === 'disengagements'
        ? HeatmapType.HeatmapTypeDisengagements
        : activeType === 'geonetCoverage'
          ? HeatmapType.HeatmapTypeGeonetCoverage
          : null

  // Fetch available filter options for the active heatmap type
  const { data: filtersData } = useErrorHandlingQuery(
    useRouteBuilderHeatmapFiltersQuery,
    {
      skip: gqlType == null,
      variables: {
        metroId: metropolisId,
        heatmapType: gqlType ?? HeatmapType.HeatmapTypePotentialCriticalEvents,
      },
    },
  )

  const onFilterOptionsChangeRef = useRef(onFilterOptionsChange)
  onFilterOptionsChangeRef.current = onFilterOptionsChange
  useEffect(() => {
    onFilterOptionsChangeRef.current?.(
      filtersData
        ? {
            eventTypes: filtersData.heatmapFilters.eventTypes,
            initiators: filtersData.heatmapFilters.initiators,
            disengagementReasons:
              filtersData.heatmapFilters.disengagementReasons,
            softwareVersions: filtersData.heatmapFilters.softwareVersions,
            earliestTimestampMs: filtersData.heatmapFilters.earliestTimestampMs,
            latestTimestampMs: filtersData.heatmapFilters.latestTimestampMs,
          }
        : null,
    )
  }, [filtersData])

  const [fetchData, { data }] = useRouteBuilderHeatmapDataLazyQuery()

  const onLoadingChangeRef = useRef(onLoadingChange)
  onLoadingChangeRef.current = onLoadingChange
  const onCategoriesChangeRef = useRef(onCategoriesChange)
  onCategoriesChangeRef.current = onCategoriesChange

  // Fetch data whenever active type or filters change.
  // Use the promise from fetchData to track completion so cached responses
  // (where `loading` stays false throughout) still reset the spinner.
  useEffect(() => {
    if (!gqlType) return
    onLoadingChangeRef.current?.(true)
    void fetchData({
      variables: {
        metroId: metropolisId,
        heatmapType: gqlType,
        filters: {
          eventTypes:
            filters.eventTypes.length > 0 ? filters.eventTypes : undefined,
          initiators:
            filters.initiators.length > 0 ? filters.initiators : undefined,
          disengagementReasons:
            filters.disengagementReasons.length > 0
              ? filters.disengagementReasons
              : undefined,
          softwareVersions:
            filters.softwareVersions.length > 0
              ? filters.softwareVersions
              : undefined,
          startTimestampMs: filters.startTimestampMs ?? undefined,
          endTimestampMs: filters.endTimestampMs ?? undefined,
        },
      },
    })
      .then(() => {
        onLoadingChangeRef.current?.(false)
      })
      .catch(() => {
        onLoadingChangeRef.current?.(false)
      })
  }, [gqlType, metropolisId, filters, fetchData])

  // Render / remove map layers whenever map, data, or active type changes
  const removeLayers = useCallback((m: maplibregl.Map) => {
    if (!m || !isMapReady(m)) return
    popupRef.current?.remove()
    popupRef.current = null
    // A removed map still exists as an object but has no style; getStyle()
    // returns undefined in that case, whereas getLayer/removeLayer throw.
    if (!m?.getStyle()) return

    for (const heatType of Object.values(HeatmapType)) {
      const srcId = SOURCE_ID[heatType]
      const heatId = LAYER_HEAT[heatType]
      const circPfx = CIRCLE_PREFIX[heatType]

      // Remove all circle sub-layers (unknown category count — iterate existing)
      if (circPfx) {
        const style = m.getStyle()
        if (style) {
          for (const layer of style.layers) {
            if (layer.id.startsWith(circPfx)) m.removeLayer(layer.id)
          }
        }
      }
      removeLayer(m, heatId)
      removeSource(m, srcId)
    }
  }, [])

  useEffect(() => {
    if (!map) return
    return () => removeLayers(map)
  }, [map, removeLayers])

  useEffect(() => {
    if (!map || !data || !activeType) return

    const addLayers = () => {
      removeLayers(map)
      if (!activeType) return

      const heatType =
        activeType === 'pce'
          ? HeatmapType.HeatmapTypePotentialCriticalEvents
          : activeType === 'disengagements'
            ? HeatmapType.HeatmapTypeDisengagements
            : HeatmapType.HeatmapTypeGeonetCoverage

      const srcId = SOURCE_ID[heatType]
      const heatId = LAYER_HEAT[heatType]
      const circPfx = CIRCLE_PREFIX[heatType]

      // Insert beneath the basemap's label layers so street names and other
      // labels remain visible on top of the heatmap. Fall back to the caller's
      // insertBeforeLayer (the route line), then to the top of the stack.
      const labelLayer = firstLabelLayerId(map)
      const beforeCandidate =
        labelLayer ??
        (insertBeforeLayer != null && map.getLayer(insertBeforeLayer)
          ? insertBeforeLayer
          : undefined)
      const before = beforeCandidate

      try {
        if (activeType === 'pce') {
          const geojson = buildPceGeoJSON(data.heatmapData.pce)
          const categories = [
            ...new Set(data.heatmapData.pce.map(p => p.eventType)),
          ]
          map.addSource(srcId, { type: 'geojson', data: geojson })
          addPceLayers(map, srcId, heatId, circPfx, categories, before)
          onCategoriesChangeRef.current?.(categories)
        } else if (activeType === 'disengagements') {
          const geojson = buildDisGeoJSON(data.heatmapData.disengagements)
          const initiators = [
            ...new Set(data.heatmapData.disengagements.map(p => p.initiator)),
          ]
          map.addSource(srcId, { type: 'geojson', data: geojson })
          addDisLayers(map, srcId, heatId, circPfx, initiators, before)
          onCategoriesChangeRef.current?.(initiators)
        } else {
          const geojson = buildCovGeoJSON(data.heatmapData.geonetCoverage)
          map.addSource(srcId, { type: 'geojson', data: geojson })
          addCovLayers(map, srcId, heatId, before)
          onCategoriesChangeRef.current?.([])
        }
      } catch (err) {
        // layer add failed — likely a style not loaded yet, styledata listener will retry
        void err
      }

      // Hover popup for coverage lines
      if (activeType === 'geonetCoverage') {
        const onMouseMove = (
          e: maplibregl.MapMouseEvent & {
            features?: maplibregl.MapGeoJSONFeature[]
          },
        ) => {
          const f = e.features?.[0]
          if (!f) return
          map.getCanvas().style.cursor = 'pointer'
          const props = f.properties
          const html = `
            <div style="font-size:12px;line-height:1.5">
              <b>${String(props.streetName || 'Road segment')}</b><br/>
              Success rate: <b>${(Number(props.successRate) * 100).toFixed(1)}%</b><br/>
              Avg duration: ${Number(props.avgDuration).toFixed(1)}s &nbsp;
              Max speed: ${Number(props.maxSpeedKmh).toFixed(0)} km/h<br/>
              Edge count: ${String(props.osmEdgeCount)} &nbsp; Version: ${String(props.swVersion)}
            </div>`
          popupRef.current?.remove()
          popupRef.current = makePopup(
            map,
            html,
            e.lngLat.toArray() as [number, number],
          )
        }
        const onMouseLeave = () => {
          map.getCanvas().style.cursor = ''
          popupRef.current?.remove()
          popupRef.current = null
        }
        map.on('mousemove', heatId, onMouseMove)
        map.on('mouseleave', heatId, onMouseLeave)
      }

      // Hover popup for PCE circles
      if (activeType === 'pce') {
        const style = map.getStyle()
        const circleLayerIds = style
          ? style.layers.filter(l => l.id.startsWith(circPfx)).map(l => l.id)
          : []
        for (const layerId of circleLayerIds) {
          const onMouseEnter = (
            e: maplibregl.MapMouseEvent & {
              features?: maplibregl.MapGeoJSONFeature[]
            },
          ) => {
            const f = e.features?.[0]
            if (!f) return
            map.getCanvas().style.cursor = 'pointer'
            const p = f.properties
            const html = `
              <div style="font-size:12px;line-height:1.5">
                <b>Event:</b> ${String(p.eventType)}<br/>
                <b>Time:</b> ${formatTimestamp(Number(p.timestampMs))}<br/>
                <b>Version:</b> ${String(p.softwareVersion)}<br/>
                <b>Annotations:</b> ${String(p.annotationCount)}
              </div>`
            popupRef.current?.remove()
            popupRef.current = makePopup(
              map,
              html,
              e.lngLat.toArray() as [number, number],
            )
          }
          const onMouseLeave = () => {
            map.getCanvas().style.cursor = ''
            popupRef.current?.remove()
            popupRef.current = null
          }
          map.on('mouseenter', layerId, onMouseEnter)
          map.on('mouseleave', layerId, onMouseLeave)
        }
      }

      // Hover popup for disengagement circles
      if (activeType === 'disengagements') {
        const style = map.getStyle()
        const circleLayerIds = style
          ? style.layers.filter(l => l.id.startsWith(circPfx)).map(l => l.id)
          : []
        for (const layerId of circleLayerIds) {
          const onMouseEnter = (
            e: maplibregl.MapMouseEvent & {
              features?: maplibregl.MapGeoJSONFeature[]
            },
          ) => {
            const f = e.features?.[0]
            if (!f) return
            map.getCanvas().style.cursor = 'pointer'
            const p = f.properties
            const html = `
              <div style="font-size:12px;line-height:1.5">
                <b>Initiator:</b> ${String(p.initiator)}<br/>
                <b>Reason:</b> ${String(p.disengagementReason)}<br/>
                <b>Time:</b> ${formatTimestamp(Number(p.timestampMs))}<br/>
                <b>Version:</b> ${String(p.softwareVersion)}
              </div>`
            popupRef.current?.remove()
            popupRef.current = makePopup(
              map,
              html,
              e.lngLat.toArray() as [number, number],
            )
          }
          const onMouseLeave = () => {
            map.getCanvas().style.cursor = ''
            popupRef.current?.remove()
            popupRef.current = null
          }
          map.on('mouseenter', layerId, onMouseEnter)
          map.on('mouseleave', layerId, onMouseLeave)
        }
      }
    }

    if (map.isStyleLoaded()) {
      addLayers()
    } else {
      map.once('styledata', addLayers)
    }
  }, [map, data, activeType, insertBeforeLayer, removeLayers])

  // Sync per-category visibility with MapLibre whenever hiddenCategories changes.
  // Two layers represent each category: the zoom-in circles AND the aggregate
  // heatmap density layer. Circles get toggled via layout visibility; the heat
  // layer (which shows all categories at low zoom) gets a feature filter that
  // excludes the hidden categories — otherwise hiding a category has no visible
  // effect until the user zooms past 14.
  useEffect(() => {
    if (!map || !activeType || !hiddenCategories) return
    const heatType =
      activeType === 'pce'
        ? HeatmapType.HeatmapTypePotentialCriticalEvents
        : activeType === 'disengagements'
          ? HeatmapType.HeatmapTypeDisengagements
          : HeatmapType.HeatmapTypeGeonetCoverage
    const circPfx = CIRCLE_PREFIX[heatType]
    if (!circPfx) return
    const style = map.getStyle()
    if (!style) return

    for (const layer of style.layers) {
      if (layer.id.startsWith(circPfx)) {
        const cat = layer.id.slice(circPfx.length + 1) // strip "prefix-"
        const visibility = hiddenCategories.has(cat) ? 'none' : 'visible'
        map.setLayoutProperty(layer.id, 'visibility', visibility)
      }
    }

    // Filter the aggregate heatmap layer to exclude hidden categories.
    const heatId = LAYER_HEAT[heatType]
    if (map.getLayer(heatId)) {
      const property = activeType === 'pce' ? 'eventType' : 'initiator'
      const hidden = [...hiddenCategories]
      const filter: maplibregl.FilterSpecification | null =
        hidden.length > 0
          ? ['!', ['in', ['get', property], ['literal', hidden]]]
          : null
      map.setFilter(heatId, filter)
    }
  }, [map, activeType, hiddenCategories])

  // Remove all layers when activeType becomes null
  useEffect(() => {
    if (!map || activeType !== null) return
    removeLayers(map)
  }, [map, activeType, removeLayers])

  return null
}
